import { useMemo, useState } from "react";
import EnlaceCotizar from "../components/ui/EnlaceCotizar.jsx";
import { useSearchParams } from "react-router-dom";
import ProductoCard from "../components/catalogo/ProductoCard.jsx";
import { useProductos, useCategorias, filtrarProductos } from "../lib/useCatalogo";
import s from "./Catalogo.module.css";

const ORDENES = [
  { valor: "relevancia", label: "Mas pedidos" },
  { valor: "nombre", label: "Nombre (A-Z)" },
  { valor: "precio-asc", label: "Precio: menor a mayor" },
  { valor: "precio-desc", label: "Precio: mayor a menor" },
];

export default function Catalogo() {
  const [params, setParams] = useSearchParams();
  const { productos, loading } = useProductos();
  const { categorias } = useCategorias();

  const [texto, setTexto] = useState(params.get("q") || "");
  const categoria = params.get("categoria") || "todos";
  const orden = params.get("orden") || "relevancia";

  const resultados = useMemo(
    () => filtrarProductos(productos, { texto, categoria, orden }),
    [productos, texto, categoria, orden]
  );

  function cambiarParam(clave, valor) {
    const siguiente = new URLSearchParams(params);
    if (!valor || valor === "todos" || valor === "relevancia") {
      siguiente.delete(clave);
    } else {
      siguiente.set(clave, valor);
    }
    setParams(siguiente, { replace: true });
  }

  const conteoPorCategoria = useMemo(() => {
    const mapa = { todos: productos.length };
    for (const p of productos) {
      mapa[p.categoria] = (mapa[p.categoria] || 0) + 1;
    }
    return mapa;
  }, [productos]);

  return (
    <>
      <header className={s.cabecera}>
        <div className="container">
          <p className="eyebrow">Catalogo</p>
          <h1 className={s.titulo}>Productos de acero</h1>
          <p className={s.subtitulo}>
            {loading
              ? "Cargando catalogo..."
              : `${resultados.length} producto${resultados.length === 1 ? "" : "s"} disponibles`}
          </p>
        </div>
      </header>

      <div className={`container ${s.cuerpo}`}>
        <div className={s.layout}>
          <aside className={s.filtros}>
            <div className={s.bloqueFiltro}>
              <label className="label" htmlFor="buscar">
                Buscar
              </label>
              <input
                id="buscar"
                type="search"
                className="input"
                placeholder="Ej. corrugada 1/2, tubo cuadrado, ASTM"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
              <p className="hint">Busca por nombre, SKU, norma o medida.</p>
            </div>

            <div className={s.bloqueFiltro}>
              <p className="label">Linea de producto</p>
              <ul className={s.listaCategorias}>
                <li>
                  <button
                    type="button"
                    className={`${s.filtroBtn} ${categoria === "todos" ? s.filtroActivo : ""}`}
                    onClick={() => cambiarParam("categoria", "todos")}
                  >
                    <span>Todas</span>
                    <span className="mono">{conteoPorCategoria.todos ?? 0}</span>
                  </button>
                </li>
                {categorias.map((c) => {
                  const id = c.slug || c.id;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={`${s.filtroBtn} ${categoria === id ? s.filtroActivo : ""}`}
                        onClick={() => cambiarParam("categoria", id)}
                      >
                        <span>{c.nombre}</span>
                        <span className="mono">{conteoPorCategoria[id] ?? 0}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <section className={s.resultados}>
            <div className={s.barraOrden}>
              <p className={s.conteo}>
                <strong>{resultados.length}</strong> resultado
                {resultados.length === 1 ? "" : "s"}
                {categoria !== "todos" && (
                  <button
                    type="button"
                    className={s.limpiar}
                    onClick={() => cambiarParam("categoria", "todos")}
                  >
                    Quitar filtro
                  </button>
                )}
              </p>

              <div className={s.selectorOrden}>
                <label className="visually-hidden" htmlFor="orden">
                  Ordenar por
                </label>
                <select
                  id="orden"
                  className="select"
                  value={orden}
                  onChange={(e) => cambiarParam("orden", e.target.value)}
                >
                  {ORDENES.map((o) => (
                    <option key={o.valor} value={o.valor}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {productos.length === 0 && !loading ? (
              /* Catalogo vacio y busqueda vacia no son lo mismo: decirle
                 "prueba con otro termino" a quien no ha buscado nada lo manda
                 a intentar algo que no va a funcionar. */
              <div className={s.vacio}>
                <h2 className={s.vacioTitulo}>Catalogo en preparacion</h2>
                <p>
                  Todavia no hay materiales publicados. Mientras tanto,
                  escribenos con tu lista y te cotizamos.
                </p>
                <EnlaceCotizar className="btn btn--accent btn--sm">
                  Pedir cotizacion
                </EnlaceCotizar>
              </div>
            ) : resultados.length === 0 ? (
              <div className={s.vacio}>
                <h2 className={s.vacioTitulo}>Sin resultados</h2>
                <p>
                  No encontramos productos para esa busqueda. Prueba con otro
                  termino o pide una cotizacion con tu lista de materiales.
                </p>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => {
                    setTexto("");
                    cambiarParam("categoria", "todos");
                  }}
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className={s.grid}>
                {resultados.map((p) => (
                  <ProductoCard key={p.id} producto={p} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
